import React, { useEffect, useState } from 'react'
import PortalTitle from '../../component/common/portal.title'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { Row, Col, Spin, Form, Input, Card, Button, Result } from 'antd'
import { ClockCircleOutlined } from '@ant-design/icons'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import '../../css/resetpassword.scss'
import serviceInterface from '../../services/service.interface'
import { setPassword } from '../../store/action/user.action'

var cardSize =
  (window.innerWidth < window.innerHeight
    ? window.innerWidth
    : window.innerHeight) - 20

cardSize = cardSize < 450 ? cardSize : 450

const Password = (props) => {
  const params = useParams()
  const location = useLocation()
  const history = useHistory()

  // 'checking' until the server says whether the link still works. A link is
  // valid for only 15 minutes (RESET_TOKEN_TTL_MINUTES), and new users often
  // open the email days later -- so check first, rather than letting them type
  // a password only to be told afterwards that the link is dead.
  const [linkState, setLinkState] = useState('checking') // checking | valid | expired

  const checkLink = () =>
    serviceInterface
      .AuthService()
      .checkPasswordLink(params['token'])
      .then((ok) => setLinkState(ok ? 'valid' : 'expired'))
      // A network failure is not proof the link is dead: show the form, and
      // let the submit (which enforces the same rule) have the final word.
      .catch(() => setLinkState('valid'))

  useEffect(() => {
    checkLink()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params['token']])

  var onFinish = ({ email, password }) => {
    props
      .changePassword(email, params['token'], password)
      .then((data) => {
        if (data) {
          history.push('/login')
          return
        }
        // The link can still die between page load and submit (it expired
        // while the form was open, or a wrong email consumed it). Re-check, so
        // the user gets the same clear way forward instead of a dead form.
        checkLink()
      })
  }

  var currentMenuKey = (key) => {
    if (location.pathname.search(key) >= 0) {
      return location.pathname
    } else {
      return key
    }
  }

  const title =
    location.pathname == currentMenuKey('/reset-password')
      ? 'Reset Password'
      : 'Set Password'

  if (linkState === 'checking') {
    return (
      <Row type='flex' align='middle' className='resetRow center passwordRow'>
        <Col span={24} flex='auto'>
          <div className='resetContainer passwordContainer'>
            <Spin tip='Checking your link…' />
          </div>
        </Col>
      </Row>
    )
  }

  if (linkState === 'expired') {
    return (
      <Row type='flex' align='middle' className='resetRow center passwordRow'>
        <Col span={24} flex='auto'>
          <div className='resetContainer passwordContainer'>
            <Card style={{ width: cardSize }}>
              <PortalTitle />
              <Result
                icon={<ClockCircleOutlined style={{ color: '#faad14' }} />}
                title='This link has expired'
                subTitle={
                  <>
                    The link to set your password has expired.
                    <br />
                    To request a new link please click here:
                  </>
                }
                extra={
                  <Button
                    type='primary'
                    size='large'
                    onClick={() => history.push('/forgot-password')}
                  >
                    Get Set Password Link
                  </Button>
                }
              />
            </Card>
          </div>
        </Col>
      </Row>
    )
  }

  return (
    <Row type='flex' align='middle' className='resetRow center passwordRow'>
      <Col span={24} flex='auto'>
        <div className='resetContainer passwordContainer'>
          <Card style={{ width: cardSize }}>
            <PortalTitle />
            <div className='header center'>
              <h1>{title}</h1>
            </div>
            <Spin spinning={!!props.isLoading}>
            <Form
              // {...formItemLayout}
              layout={'vertical'}
              name='normal_login'
              className='login-form'
              initialValues={{ remember: true }}
              onFinish={(value) => onFinish(value)}
            >
              <Form.Item
                label='Email'
                name='email'
                rules={[
                  {
                    required: true,
                    message: 'Please input your Password!',
                  },
                ]}
              >
                <Input placeholder='Email' />
              </Form.Item>
              <Form.Item
                label='New Password'
                name='password'
                rules={[
                  {
                    required: true,
                    message: 'Please input your Password!',
                  },
                  {
                    min: 8,
                    message: 'Password must be at least 8 characters long!',
                  },
                  {
                    pattern: /(?=.*[A-Z])/,
                    message: 'Password must contain at least one uppercase letter!',
                  },
                  {
                    pattern: /(?=.*[0-9])/,
                    message: 'Password must contain at least one number!',
                  },
                  {
                    pattern: /(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?])/,
                    message: 'Password must contain at least one special character!',
                  },
                ]}
                help='Password must be at least 8 characters with one uppercase letter, one number, and one special character'
              >
                <Input.Password placeholder='Password' />
              </Form.Item>
              <Form.Item
                label='Confirm Password'
                name='confirmpassword'
                hasFeedback
                dependencies={['newpassword']}
                rules={[
                  {
                    required: true,
                    message: 'Please confirm your password!',
                  },
                  ({ getFieldValue }) => ({
                    validator(rule, value) {
                      if (!value || getFieldValue('password') === value) {
                        return Promise.resolve()
                      }
                      return Promise.reject(
                        'The two passwords that you entered do not match!'
                      )
                    },
                  }),
                ]}
              >
                <Input.Password placeholder='Password' />
              </Form.Item>
              <Form.Item>
                <Button htmlType='submit' className='submitBtn'>
                  Submit
                </Button>
              </Form.Item>
            </Form>
            </Spin>
          </Card>
        </div>
      </Col>
    </Row>
  )
}

Password.propTypes = {
  resetPassword: PropTypes.func.isRequired,
  hasError: PropTypes.bool,
  isLoading: PropTypes.bool,
  message: PropTypes.string,
  successMessage: PropTypes.string,
}

const mapStateToProps = (state) => {
  return {
    hasError: state.profile.resetError,
    message: state.profile.message,
    successMessage: state.profile.successMessage,
    isLoading: state.profile.isLoading,
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    changePassword: (email, token, password) =>
      dispatch(setPassword(email, token, password)),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(Password)
